import React, { useState, useEffect } from 'react'
import styles from './MediaModal.module.scss'
import VideoPlayer from '../../../../../components/VideoPlayer/VideoPlayer'
import MediaStats from '../MediaStats/MediaStats'
import { GameControllerIcon, FilmIcon, TVIcon, CalendarIcon, DocumentIcon } from '../../../../../components/CustomIcons'
import MediaService from '../../../../../service/MediaService'

export default function MediaModal({ media, open, onClose }) {
  const [mediaData, setMediaData] = useState(media || null)
  const [likeCount, setLikeCount] = useState(media?.likes_count || 0)
  const [isLiked, setIsLiked] = useState(media?.is_liked || false)
  const [userRating, setUserRating] = useState(media?.user_rating || null)
  const [isLikeLoading, setIsLikeLoading] = useState(false)
  const [isRating, setIsRating] = useState(false)

  useEffect(() => {
    if (!media) return

    setMediaData(media)
    setLikeCount(media?.likes_count || 0)
    setIsLiked(media?.is_liked || false)
    setUserRating(media?.user_rating || null)
  }, [media])

  useEffect(() => {
    if (!open || !media) return

    const loadMediaDetails = async () => {
      try {
        let response
        switch (media.type) {
          case 'press':
            response = await MediaService.getPressArticle(media.slug || media.id)
            break
          case 'news':
            response = await MediaService.getNewsArticle(media.slug || media.id)
            break
          case 'reviews':
            response = await MediaService.getVideoReview(media.slug || media.id)
            break
          case 'letsplay':
            response = await MediaService.getLetsPlayVideo(media.slug || media.id)
            break
          case 'streamers':
            response = await MediaService.getStreamer(media.slug || media.id)
            break
          default:
            response = null
            break
        }

        const payload = response?.data || response || null
        if (!payload) return

        const mergedData = {
          ...media,
          ...payload,
          type: media.type
        }

        setMediaData(mergedData)
        setLikeCount(mergedData.likes_count || 0)
        setIsLiked(Boolean(mergedData.is_liked))
        setUserRating(mergedData.user_rating ?? null)
      } catch (error) {
        console.error('Error loading media details:', error)
      }
    }

    loadMediaDetails()
  }, [open, media])

  useEffect(() => {
    if (open) {
      document.body.style.overflow = 'hidden'
      
      const handleEscape = (e) => {
        if (e.key === 'Escape') {
          onClose()
        }
      }
      
      document.addEventListener('keydown', handleEscape)
      return () => {
        document.removeEventListener('keydown', handleEscape)
      }
    }
    return () => {
      document.body.style.overflow = 'unset'
    }
  }, [open, onClose])

  if (!open || !media) return null

  const currentMedia = mediaData || media

  const getContentIcon = (contentType) => {
    switch (contentType) {
      case 'press': return <DocumentIcon size={20} />
      case 'news': return <DocumentIcon size={20} />
      case 'reviews': return <FilmIcon size={20} />
      case 'letsplay': return <GameControllerIcon size={20} />
      case 'streamers': return <TVIcon size={20} />
      default: return <DocumentIcon size={20} />
    }
  }

  const getContentTypeLabel = (contentType) => {
    switch (contentType) {
      case 'press': return 'Пресс-статья'
      case 'news': return 'Новость'
      case 'reviews': return 'Видео-обзор'
      case 'letsplay': return "Let's Play"
      case 'streamers': return 'Стример'
      default: return 'Контент'
    }
  }

  const handleRate = async (rating) => {
    if (isRating) return
    
    setIsRating(true)
    try {
      let rateFunction
      switch (currentMedia.type) {
        case 'reviews':
          rateFunction = MediaService.rateVideoReview
          break
        case 'letsplay':
          rateFunction = MediaService.rateLetsPlayVideo
          break
        case 'news':
          rateFunction = MediaService.rateNewsArticle
          break
        case 'streamers':
          rateFunction = MediaService.rateStreamer
          break
        default:
          return
      }
      
      const response = await rateFunction(currentMedia.slug || currentMedia.id, rating)
      setUserRating(rating)

      const averageRating = response?.data?.average_rating
      if (averageRating !== undefined) {
        setMediaData((prev) => ({
          ...(prev || currentMedia),
          rating_avg: averageRating
        }))
      }
    } catch (error) {
      console.error('Error rating media:', error)
    } finally {
      setIsRating(false)
    }
  }

  const handleToggleLike = async () => {
    if (isLikeLoading) return

    try {
      setIsLikeLoading(true)

      let likeFunction
      switch (currentMedia.type) {
        case 'press':
          likeFunction = MediaService.likePressArticle
          break
        case 'news':
          likeFunction = MediaService.likeNewsArticle
          break
        case 'reviews':
          likeFunction = MediaService.likeVideoReview
          break
        case 'letsplay':
          likeFunction = MediaService.likeLetsPlayVideo
          break
        case 'streamers':
          likeFunction = MediaService.likeStreamer
          break
        default:
          likeFunction = null
      }

      if (!likeFunction) return

      const response = await likeFunction(currentMedia.slug || currentMedia.id)
      const liked = Boolean(response?.data?.liked)
      const nextLikeCount = response?.data?.likes_count ?? (liked ? likeCount + 1 : Math.max(0, likeCount - 1))

      setIsLiked(liked)
      setLikeCount(nextLikeCount)
      setMediaData((prev) => ({
        ...(prev || currentMedia),
        is_liked: liked,
        likes_count: nextLikeCount
      }))
    } catch (error) {
      console.error('Error toggling media like:', error)
    } finally {
      setIsLikeLoading(false)
    }
  }

  const hasVideo = currentMedia.video_file || currentMedia.video_url
  const videoSource = currentMedia.video_file || currentMedia.video_url
  const sourceType = currentMedia.video_file ? 'file' : 'url'

  return (
    <div className={styles.modalOverlay} onClick={onClose}>
      <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
        <div className={styles.modalHeader}>
          <div className={styles.mediaIcon}>
            {getContentIcon(currentMedia.type)}
          </div>
          <h3>{currentMedia.title || currentMedia.username || currentMedia.name}</h3>
          <button className={styles.closeButton} onClick={onClose}>×</button>
        </div>

        <div className={styles.modalBody}>
          <div className={styles.contentType}>
            {getContentTypeLabel(currentMedia.type)}
          </div>

          {/* Cover Image */}
          {currentMedia.cover_image && !hasVideo && (
            <div className={styles.coverImageSection}>
              <img src={currentMedia.cover_image} alt={currentMedia.title} className={styles.coverImage} />
            </div>
          )}

          {/* Video Player for reviews and let's play */}
          {hasVideo && (
            <div className={styles.videoSection}>
              <VideoPlayer
                sourceType={sourceType}
                src={videoSource}
                poster={currentMedia.cover_image}
                title={currentMedia.title}
                episodeMeta={{
                  type: getContentTypeLabel(currentMedia.type),
                  duration: currentMedia.duration_min
                }}
                autoPlay={false}
              />
            </div>
          )}

          {currentMedia.description && (
            <div className={styles.description}>
              <h4>Описание:</h4>
              <p>{currentMedia.description}</p>
            </div>
          )}

          {currentMedia.body && (
            <div className={styles.content}>
              <h4>Содержание:</h4>
              <div className={styles.contentText}>{currentMedia.body}</div>
            </div>
          )}

          {currentMedia.stream_url && (
            <div className={styles.streamSection}>
              <h4>Прямая трансляция:</h4>
              <a href={currentMedia.stream_url} target="_blank" rel="noopener noreferrer" className={styles.streamLink}>
                Смотреть стрим
              </a>
            </div>
          )}

          {currentMedia.tags && currentMedia.tags.length > 0 && (
            <div className={styles.tags}>
              <h4>Теги:</h4>
              <div className={styles.tagList}>
                {currentMedia.tags.map((tag, index) => (
                  <span key={index} className={styles.tag}>#{tag}</span>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className={styles.statsBar}>
          <MediaStats
            media={currentMedia}
            onRate={handleRate}
            userRating={userRating}
            stats={{ likes_count: likeCount, followers_count: currentMedia.followers_count }}
            onLike={handleToggleLike}
            isLiked={isLiked}
            isLikeLoading={isLikeLoading}
          />
        </div>

        <div className={styles.modalFooter}>
          <button className={styles.closeBtn} onClick={onClose}>
            Закрыть
          </button>
        </div>
      </div>
    </div>
  )
}